// React hook for header icon analytics tracking
// Provides easy access to navigation tracking functions with proper TypeScript support

import {
  trackNavigation,
  trackError,
  trackPerformance,
  trackEvent,
} from '../utils/analytics';
import {
  NAVIGATION_EVENTS,
  NOTIFICATION_EVENTS,
  USER_PROFILE_EVENTS,
  HEADER_EVENTS,
  TOUR_EVENTS,
  BULK_ACTIONS_EVENTS,
  createEventProperties,
  getCardDisplayName,
  type UserProfileCardType,
  type NotificationEventProperties,
  type BulkActionsEventProperties,
} from '../utils/eventTracking';
import {
  getTourDisplayName,
  getStepCategory,
  getPageContext,
  logTourEvent,
} from '../utils/tourUtils';

export const useEventTracking = (): {
  trackPageView: (
    properties?: Parameters<typeof createEventProperties>[0]
  ) => void;
  trackHeaderIconClick: (
    iconName: string,
    properties?: Parameters<typeof createEventProperties>[0]
  ) => void;
  trackDropdownAction: (
    headerIconName: string,
    action: string,
    properties?: Parameters<typeof createEventProperties>[0]
  ) => void;
  trackNotificationClick: (
    notificationType: string,
    notificationId: string,
    properties?: Partial<NotificationEventProperties>
  ) => void;
  trackNotificationView: (
    notificationType: string,
    notificationId: string,
    properties?: Partial<NotificationEventProperties>
  ) => void;
  trackWatchlistAdd: (
    certificationIds: number[],
    properties?: Parameters<typeof createEventProperties>[0]
  ) => void;
  trackWatchlistRemove: (
    certificationIds: number[],
    properties?: Parameters<typeof createEventProperties>[0]
  ) => void;

  // New notification page tracking functions
  trackNotificationPageView: (
    properties?: Partial<NotificationEventProperties>
  ) => void;
  trackNotificationMarkRead: (
    notificationId: number,
    properties?: Partial<NotificationEventProperties>
  ) => void;
  trackNotificationMarkUnread: (
    notificationId: number,
    properties?: Partial<NotificationEventProperties>
  ) => void;
  trackNotificationSelect: (
    notificationId: number,
    selected: boolean,
    properties?: Partial<NotificationEventProperties>
  ) => void;
  trackNotificationBulkAction: (
    actionType: 'mark_read' | 'mark_unread',
    selectedCount: number,
    properties?: Partial<NotificationEventProperties>
  ) => void;
  trackNotificationFilterChange: (
    filterType: string,
    filterValue: string,
    properties?: Partial<NotificationEventProperties>
  ) => void;
  trackNotificationPagination: (
    page: number,
    totalPages: number,
    properties?: Partial<NotificationEventProperties>
  ) => void;
  trackNotificationRefresh: (
    properties?: Partial<NotificationEventProperties>
  ) => void;
  trackNotificationHelpClick: (
    properties?: Partial<NotificationEventProperties>
  ) => void;
  trackNotificationTourClick: (
    properties?: Partial<NotificationEventProperties>
  ) => void;
  trackNotificationSettingsClick: (
    properties?: Partial<NotificationEventProperties>
  ) => void;
  trackNotificationAlertDismiss: (
    properties?: Partial<NotificationEventProperties>
  ) => void;
  trackNotificationBulkDetailsOpen: (
    notificationId: number,
    properties?: Partial<NotificationEventProperties>
  ) => void;
  trackNotificationBulkDetailsClose: (
    notificationId: number,
    properties?: Partial<NotificationEventProperties>
  ) => void;
  trackNotificationCardExpand: (
    cardName: string,
    properties?: Partial<NotificationEventProperties>
  ) => void;
  trackNotificationCardCollapse: (
    cardName: string,
    properties?: Partial<NotificationEventProperties>
  ) => void;

  // Bulk actions tracking functions
  trackBulkStatusModalOpen: (
    certificationCount: number,
    certificationConfigsCount: number,
    properties?: Partial<BulkActionsEventProperties>
  ) => void;
  trackBulkStatusModalClose: (
    properties?: Partial<BulkActionsEventProperties>
  ) => void;
  trackBulkStatusUpdateStart: (
    certificationCount: number,
    certificationConfigsCount: number,
    properties?: Partial<BulkActionsEventProperties>
  ) => void;
  trackBulkStatusUpdateSuccess: (
    successCount: number,
    failureCount: number,
    properties?: Partial<BulkActionsEventProperties>
  ) => void;
  trackBulkStatusUpdateFailure: (
    errorMessage: string,
    properties?: Partial<BulkActionsEventProperties>
  ) => void;
  trackBulkStatusSelectorChange: (
    configId: number,
    configName: string,
    newStatusId: number,
    newStatusName: string,
    properties?: Partial<BulkActionsEventProperties>
  ) => void;
  trackBulkStatusHelpDismiss: (
    properties?: Partial<BulkActionsEventProperties>
  ) => void;
  trackBulkStatusHelpShow: (
    properties?: Partial<BulkActionsEventProperties>
  ) => void;

  trackBulkFileOwnerModalOpen: (
    certificationCount: number,
    propertiesCount: number,
    properties?: Partial<BulkActionsEventProperties>
  ) => void;
  trackBulkFileOwnerModalClose: (
    certificationCount: number,
    propertiesCount: number,
    properties?: Partial<BulkActionsEventProperties>
  ) => void;
  trackBulkFileOwnerUpdateStart: (
    certificationCount: number,
    propertiesCount: number,
    properties?: Partial<BulkActionsEventProperties>
  ) => void;
  trackBulkFileOwnerUpdateSuccess: (
    successCount: number,
    failureCount: number,
    properties?: Partial<BulkActionsEventProperties>
  ) => void;
  trackBulkFileOwnerUpdateFailure: (
    errorMessage: string,
    properties?: Partial<BulkActionsEventProperties>
  ) => void;
  trackBulkFileOwnerSelectorChange: (
    propertyId: number,
    fileOwnerId: number | null,
    properties?: Partial<BulkActionsEventProperties>
  ) => void;
  trackBulkFileOwnerHelpDismiss: (
    properties?: Partial<BulkActionsEventProperties>
  ) => void;
  trackBulkFileOwnerHelpShow: (
    properties?: Partial<BulkActionsEventProperties>
  ) => void;

  // Bulk dates tracking functions
  trackBulkDatesModalOpen: (
    certificationCount: number,
    properties?: Partial<BulkActionsEventProperties>
  ) => void;
  trackBulkDatesModalClose: (
    certificationCount: number,
    properties?: Partial<BulkActionsEventProperties>
  ) => void;
  trackBulkDatesUpdateStart: (
    certificationCount: number,
    properties?: Partial<BulkActionsEventProperties>
  ) => void;
  trackBulkDatesUpdateSuccess: (
    certificationCount: number,
    successCount: number,
    failureCount: number,
    properties?: Partial<BulkActionsEventProperties>
  ) => void;
  trackBulkDatesUpdateFailure: (
    certificationCount: number,
    properties?: Partial<BulkActionsEventProperties> & {
      error_message: string;
    }
  ) => void;
  trackBulkDatesSelectorChange: (
    properties?: Partial<BulkActionsEventProperties>
  ) => void;
  trackBulkDatesHelpShow: (
    properties?: Partial<BulkActionsEventProperties>
  ) => void;

  // User Profile specific tracking
  trackCardHelpClick: (
    cardName: UserProfileCardType,
    helpType: string,
    componentName?: string,
    functionName?: string
  ) => void;
  trackCardExpand: (
    cardName: UserProfileCardType,
    componentName?: string,
    functionName?: string
  ) => void;
  trackCardCollapse: (
    cardName: UserProfileCardType,
    componentName?: string,
    functionName?: string
  ) => void;
  trackCardSettingChange: (
    cardName: UserProfileCardType,
    settingCategory: string,
    settingName: string,
    oldValue: unknown,
    newValue: unknown,
    componentName?: string,
    functionName?: string
  ) => void;
  trackCardFormSave: (
    cardName: UserProfileCardType,
    success: boolean,
    errors?: string[],
    componentName?: string,
    functionName?: string
  ) => void;
  trackCardFormCancel: (
    cardName: UserProfileCardType,
    componentName?: string,
    functionName?: string
  ) => void;
  trackPasswordReset: (
    cardName: UserProfileCardType,
    componentName?: string,
    functionName?: string
  ) => void;
  trackEmailChange: (
    cardName: UserProfileCardType,
    oldEmail: string,
    newEmail: string,
    componentName?: string,
    functionName?: string
  ) => void;
  trackPhoneChange: (
    cardName: UserProfileCardType,
    oldPhone: string,
    newPhone: string,
    componentName?: string,
    functionName?: string
  ) => void;
  trackContactEmailAdd: (
    cardName: UserProfileCardType,
    email: string,
    componentName?: string,
    functionName?: string
  ) => void;
  trackContactEmailDelete: (
    cardName: UserProfileCardType,
    email: string,
    componentName?: string,
    functionName?: string
  ) => void;
  trackContactEmailSetPrimary: (
    cardName: UserProfileCardType,
    email: string,
    componentName?: string,
    functionName?: string
  ) => void;
  trackContactPhoneAdd: (
    cardName: UserProfileCardType,
    phone: string,
    componentName?: string,
    functionName?: string
  ) => void;
  trackContactPhoneDelete: (
    cardName: UserProfileCardType,
    phone: string,
    componentName?: string,
    functionName?: string
  ) => void;
  trackContactPhoneSetPrimary: (
    cardName: UserProfileCardType,
    phone: string,
    componentName?: string,
    functionName?: string
  ) => void;
  trackHelpModalOpen: (
    cardName: UserProfileCardType,
    modalType: string,
    componentName?: string,
    functionName?: string
  ) => void;
  trackHelpModalClose: (
    cardName: UserProfileCardType,
    modalType: string,
    componentName?: string,
    functionName?: string
  ) => void;

  // Tour specific tracking
  trackTourStart: (
    tourName: string,
    totalSteps: number,
    stepName?: string,
    componentName?: string,
    functionName?: string
  ) => void;
  trackTourComplete: (
    tourName: string,
    stepsCompleted: number,
    stepName?: string,
    componentName?: string,
    functionName?: string
  ) => void;
  trackTourSkip: (
    tourName: string,
    stepNumber: number,
    stepName?: string,
    totalSteps?: number,
    componentName?: string,
    functionName?: string
  ) => void;
  trackTourStepAdvance: (
    tourName: string,
    stepNumber: number,
    stepName: string,
    componentName?: string,
    functionName?: string
  ) => void;
  trackTourStepBack: (
    tourName: string,
    stepNumber: number,
    stepName: string,
    componentName?: string,
    functionName?: string
  ) => void;

  // Enhanced tour tracking functions
  trackTourPause: (
    tourName: string,
    stepNumber: number,
    stepName?: string,
    totalSteps?: number,
    componentName?: string,
    functionName?: string
  ) => void;
  trackTourResume: (
    tourName: string,
    stepNumber: number,
    stepName?: string,
    totalSteps?: number,
    componentName?: string,
    functionName?: string
  ) => void;
  trackTourAutoAdvance: (
    tourName: string,
    stepNumber: number,
    stepName: string,
    totalSteps?: number,
    componentName?: string,
    functionName?: string
  ) => void;
  trackTourStepView: (
    tourName: string,
    stepNumber: number,
    stepName: string,
    stepSelector: string,
    totalSteps?: number,
    componentName?: string,
    functionName?: string
  ) => void;
  trackTourClose: (
    tourName: string,
    stepNumber: number,
    stepsCompleted: number,
    stepName?: string,
    componentName?: string,
    functionName?: string
  ) => void;

  // Header tracking functions
  trackHeaderHelpClick: (
    pageContext: string,
    componentName?: string,
    functionName?: string
  ) => void;
  trackHeaderTourClick: (
    pageContext: string,
    componentName?: string,
    functionName?: string
  ) => void;

  trackError: typeof trackError;
  trackPerformance: typeof trackPerformance;
} => {
  // Helper function to create debug info
  const createDebugInfo = (
    componentName?: string,
    functionName?: string
  ): {
    component_name?: string;
    function_name?: string;
    timestamp: string;
  } => ({
    component_name: componentName,
    function_name: functionName,
    timestamp: new Date().toISOString(),
  });

  return {
    // Page view tracking
    trackPageView: (
      properties: Parameters<typeof createEventProperties>[0] = {}
    ) => trackNavigation(NAVIGATION_EVENTS.PAGE_VIEW, properties),

    // Header icon click tracking
    trackHeaderIconClick: (
      iconName: string,
      properties: Parameters<typeof createEventProperties>[0] = {}
    ) =>
      trackNavigation(NAVIGATION_EVENTS.HEADER_ICON_CLICK, {
        header_icon_name: iconName,
        ...properties,
      }),

    // Dropdown action tracking
    trackDropdownAction: (
      headerIconName: string,
      action: string,
      properties: Parameters<typeof createEventProperties>[0] = {}
    ) =>
      trackNavigation(NAVIGATION_EVENTS.DROPDOWN_ACTION, {
        source: 'header_icon',
        header_icon_name: headerIconName,
        action,
        ...properties,
      }),

    // Notification tracking (keeping existing functions for compatibility)
    trackNotificationClick: (
      notificationType: string,
      notificationId: string,
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_CLICK, {
        notification_type: notificationType,
        notification_id: Number(notificationId),
        action: 'notification_click',
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },

    trackNotificationView: (
      notificationType: string,
      notificationId: string,
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_VIEW, {
        notification_type: notificationType,
        notification_id: Number(notificationId),
        action_type: 'notification_view',
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },

    // Watchlist tracking (keeping existing functions for compatibility)
    trackWatchlistAdd: (
      certificationIds: number[],
      properties: Parameters<typeof createEventProperties>[0] = {}
    ) =>
      trackNavigation(NAVIGATION_EVENTS.DROPDOWN_ACTION, {
        source: 'watch_toggle',
        action: 'add_to_watchlist',
        certification_ids: certificationIds,
        ...properties,
      }),

    trackWatchlistRemove: (
      certificationIds: number[],
      properties: Parameters<typeof createEventProperties>[0] = {}
    ) =>
      trackNavigation(NAVIGATION_EVENTS.DROPDOWN_ACTION, {
        source: 'watch_toggle',
        action: 'remove_from_watchlist',
        certification_ids: certificationIds,
        ...properties,
      }),

    // New notification page tracking functions
    trackNotificationPageView: (
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_VIEW, {
        action_type: 'notification_view',
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },

    trackNotificationMarkRead: (
      notificationId: number,
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_MARK_READ, {
        notification_id: notificationId,
        action: 'mark_read',
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },

    trackNotificationMarkUnread: (
      notificationId: number,
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_MARK_UNREAD, {
        notification_id: notificationId,
        action: 'mark_unread',
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },

    trackNotificationSelect: (
      notificationId: number,
      selected: boolean,
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_SELECT, {
        notification_id: notificationId,
        action_type: 'select',
        selected,
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },

    trackNotificationBulkAction: (
      actionType: 'mark_read' | 'mark_unread',
      selectedCount: number,
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_BULK_ACTION, {
        bulk_action_type: actionType,
        selected_count: selectedCount,
        action_type: 'bulk_action',
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },

    trackNotificationFilterChange: (
      filterType: string,
      filterValue: string,
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_FILTER_CHANGE, {
        action: 'filter_change',
        filter_type: filterType,
        filter_value: filterValue,
        card_name: 'filters',
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },

    trackNotificationPagination: (
      page: number,
      totalPages: number,
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_PAGINATION, {
        current_page: page,
        total_pages: totalPages,
        action_type: 'pagination',
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },

    trackNotificationRefresh: (
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_REFRESH, {
        action_type: 'refresh',
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },

    trackNotificationHelpClick: (
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_HELP_CLICK, {
        action_type: 'help_click',
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },

    trackNotificationTourClick: (
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_TOUR_CLICK, {
        action_type: 'tour_click',
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },

    trackNotificationSettingsClick: (
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_SETTINGS_CLICK, {
        action_type: 'settings_click',
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },

    trackNotificationAlertDismiss: (
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_ALERT_DISMISS, {
        action_type: 'alert_dismiss',
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },

    trackNotificationBulkDetailsOpen: (
      notificationId: number,
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_BULK_DETAILS_OPEN, {
        notification_id: notificationId,
        action_type: 'bulk_details_open',
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },

    trackNotificationBulkDetailsClose: (
      notificationId: number,
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_BULK_DETAILS_CLOSE, {
        notification_id: notificationId,
        action_type: 'bulk_details_close',
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },

    trackNotificationCardExpand: (
      cardName: string,
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_CARD_INTERACTION, {
        card_name: cardName,
        action: 'expand',
        card_expanded: true,
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },

    trackNotificationCardCollapse: (
      cardName: string,
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_CARD_INTERACTION, {
        card_name: cardName,
        action: 'collapse',
        card_expanded: false,
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },

    // Bulk actions tracking functions
    trackBulkStatusModalOpen: (
      certificationCount: number,
      certificationConfigsCount: number,
      properties: Partial<BulkActionsEventProperties> = {}
    ) => {
      trackEvent(BULK_ACTIONS_EVENTS.BULK_STATUS_MODAL_OPEN, {
        action: 'modal_open',
        certification_count: certificationCount,
        certification_configs_count: certificationConfigsCount,
        page_context: 'renters_dashboard',
        detail_type: 'bulk_actions',
        debug_info: {
          component_name: 'TeamRentersBulkStatusModal',
          function_name: 'useEffect',
          timestamp: new Date().toISOString(),
        },
        ...properties,
      } as BulkActionsEventProperties);
    },
    trackBulkStatusModalClose: (
      properties: Partial<BulkActionsEventProperties> = {}
    ) => {
      trackEvent(BULK_ACTIONS_EVENTS.BULK_STATUS_MODAL_CLOSE, {
        action: 'modal_close',
        page_context: 'renters_dashboard',
        detail_type: 'bulk_actions',
        debug_info: {
          component_name: 'TeamRentersBulkStatusModal',
          function_name: 'handleDialogClose',
          timestamp: new Date().toISOString(),
        },
        ...properties,
      } as BulkActionsEventProperties);
    },
    trackBulkStatusUpdateStart: (
      certificationCount: number,
      certificationConfigsCount: number,
      properties: Partial<BulkActionsEventProperties> = {}
    ) => {
      trackEvent(BULK_ACTIONS_EVENTS.BULK_STATUS_UPDATE_START, {
        action: 'update_start',
        certification_count: certificationCount,
        certification_configs_count: certificationConfigsCount,
        status_update_type: 'bulk_status_update',
        page_context: 'renters_dashboard',
        detail_type: 'bulk_actions',
        debug_info: {
          component_name: 'TeamRentersBulkStatusModal',
          function_name: 'handleSubmit',
          timestamp: new Date().toISOString(),
        },
        ...properties,
      } as BulkActionsEventProperties);
    },
    trackBulkStatusUpdateSuccess: (
      successCount: number,
      failureCount: number,
      properties: Partial<BulkActionsEventProperties> = {}
    ) => {
      trackEvent(BULK_ACTIONS_EVENTS.BULK_STATUS_UPDATE_SUCCESS, {
        action: 'update_success',
        success_count: successCount,
        failure_count: failureCount,
        status_update_type: 'bulk_status_update',
        page_context: 'renters_dashboard',
        detail_type: 'bulk_actions',
        debug_info: {
          component_name: 'TeamRentersBulkStatusModal',
          function_name: 'handleSubmit',
          timestamp: new Date().toISOString(),
        },
        ...properties,
      } as BulkActionsEventProperties);
    },
    trackBulkStatusUpdateFailure: (
      errorMessage: string,
      properties: Partial<BulkActionsEventProperties> = {}
    ) => {
      trackEvent(BULK_ACTIONS_EVENTS.BULK_STATUS_UPDATE_FAILURE, {
        action: 'update_failure',
        error_message: errorMessage,
        status_update_type: 'bulk_status_update',
        page_context: 'renters_dashboard',
        detail_type: 'bulk_actions',
        debug_info: {
          component_name: 'TeamRentersBulkStatusModal',
          function_name: 'handleSubmit',
          timestamp: new Date().toISOString(),
        },
        ...properties,
      } as BulkActionsEventProperties);
    },
    trackBulkStatusSelectorChange: (
      configId: number,
      configName: string,
      newStatusId: number,
      newStatusName: string,
      properties: Partial<BulkActionsEventProperties> = {}
    ) => {
      trackEvent(BULK_ACTIONS_EVENTS.BULK_STATUS_SELECTOR_CHANGE, {
        action: 'selector_change',
        status_update_type: 'bulk_status_update',
        page_context: 'renters_dashboard',
        detail_type: 'bulk_actions',
        debug_info: {
          component_name: 'TeamRentersBulkStatusModal',
          function_name: 'handleSelect',
          timestamp: new Date().toISOString(),
        },
        ...properties,
      } as BulkActionsEventProperties);
    },
    trackBulkStatusHelpDismiss: (
      properties: Partial<BulkActionsEventProperties> = {}
    ) => {
      trackEvent(BULK_ACTIONS_EVENTS.BULK_STATUS_HELP_DISMISS, {
        action: 'help_dismiss',
        page_context: 'renters_dashboard',
        detail_type: 'bulk_actions',
        debug_info: {
          component_name: 'TeamRentersBulkStatusModal',
          function_name: 'handleHelpDismiss',
          timestamp: new Date().toISOString(),
        },
        ...properties,
      } as BulkActionsEventProperties);
    },
    trackBulkStatusHelpShow: (
      properties: Partial<BulkActionsEventProperties> = {}
    ) => {
      trackEvent(BULK_ACTIONS_EVENTS.BULK_STATUS_HELP_SHOW, {
        action: 'help_show',
        page_context: 'renters_dashboard',
        detail_type: 'bulk_actions',
        debug_info: {
          component_name: 'TeamRentersBulkStatusModal',
          function_name: 'useEffect',
          timestamp: new Date().toISOString(),
        },
        ...properties,
      } as BulkActionsEventProperties);
    },

    trackBulkFileOwnerModalOpen: (
      certificationCount: number,
      propertiesCount: number,
      properties: Partial<BulkActionsEventProperties> = {}
    ) => {
      trackEvent(BULK_ACTIONS_EVENTS.BULK_FILE_OWNER_MODAL_OPEN, {
        action: 'modal_open',
        certification_count: certificationCount,
        properties_count: propertiesCount,
        file_owner_update_type: 'bulk_file_owner_update',
        page_context: 'renters_dashboard',
        detail_type: 'bulk_actions',
        debug_info: {
          component_name: 'TeamRentersBulkFileOwnerModal',
          function_name: 'useEffect',
          timestamp: new Date().toISOString(),
        },
        ...properties,
      } as BulkActionsEventProperties);
    },

    trackBulkFileOwnerModalClose: (
      certificationCount: number,
      propertiesCount: number,
      properties: Partial<BulkActionsEventProperties> = {}
    ) => {
      trackEvent(BULK_ACTIONS_EVENTS.BULK_FILE_OWNER_MODAL_CLOSE, {
        action: 'modal_close',
        certification_count: certificationCount,
        properties_count: propertiesCount,
        file_owner_update_type: 'bulk_file_owner_update',
        page_context: 'renters_dashboard',
        detail_type: 'bulk_actions',
        debug_info: {
          component_name: 'TeamRentersBulkFileOwnerModal',
          function_name: 'handleDialogClose',
          timestamp: new Date().toISOString(),
        },
        ...properties,
      } as BulkActionsEventProperties);
    },

    trackBulkFileOwnerUpdateStart: (
      certificationCount: number,
      propertiesCount: number,
      properties: Partial<BulkActionsEventProperties> = {}
    ) => {
      trackEvent(BULK_ACTIONS_EVENTS.BULK_FILE_OWNER_UPDATE_START, {
        action: 'update_start',
        certification_count: certificationCount,
        properties_count: propertiesCount,
        file_owner_update_type: 'bulk_file_owner_update',
        page_context: 'renters_dashboard',
        detail_type: 'bulk_actions',
        debug_info: {
          component_name: 'TeamRentersBulkFileOwnerModal',
          function_name: 'handleSubmit',
          timestamp: new Date().toISOString(),
        },
        ...properties,
      } as BulkActionsEventProperties);
    },

    trackBulkFileOwnerUpdateSuccess: (
      successCount: number,
      failureCount: number,
      properties: Partial<BulkActionsEventProperties> = {}
    ) => {
      trackEvent(BULK_ACTIONS_EVENTS.BULK_FILE_OWNER_UPDATE_SUCCESS, {
        action: 'update_success',
        success_count: successCount,
        failure_count: failureCount,
        file_owner_update_type: 'bulk_file_owner_update',
        page_context: 'renters_dashboard',
        detail_type: 'bulk_actions',
        debug_info: {
          component_name: 'TeamRentersBulkFileOwnerModal',
          function_name: 'handleSubmit',
          timestamp: new Date().toISOString(),
        },
        ...properties,
      } as BulkActionsEventProperties);
    },

    trackBulkFileOwnerUpdateFailure: (
      errorMessage: string,
      properties: Partial<BulkActionsEventProperties> = {}
    ) => {
      trackEvent(BULK_ACTIONS_EVENTS.BULK_FILE_OWNER_UPDATE_FAILURE, {
        action: 'update_failure',
        error_message: errorMessage,
        file_owner_update_type: 'bulk_file_owner_update',
        page_context: 'renters_dashboard',
        detail_type: 'bulk_actions',
        debug_info: {
          component_name: 'TeamRentersBulkFileOwnerModal',
          function_name: 'handleSubmit',
          timestamp: new Date().toISOString(),
        },
        ...properties,
      } as BulkActionsEventProperties);
    },

    trackBulkFileOwnerSelectorChange: (
      propertyId: number,
      fileOwnerId: number | null,
      properties: Partial<BulkActionsEventProperties> = {}
    ) => {
      trackEvent(BULK_ACTIONS_EVENTS.BULK_FILE_OWNER_SELECTOR_CHANGE, {
        action: 'selector_change',
        property_id: propertyId,
        file_owner_id: fileOwnerId,
        file_owner_update_type: 'bulk_file_owner_update',
        page_context: 'renters_dashboard',
        detail_type: 'bulk_actions',
        debug_info: {
          component_name: 'TeamRentersBulkFileOwnerModal',
          function_name: 'handleSelect',
          timestamp: new Date().toISOString(),
        },
        ...properties,
      } as BulkActionsEventProperties);
    },

    trackBulkFileOwnerHelpDismiss: (
      properties: Partial<BulkActionsEventProperties> = {}
    ) => {
      trackEvent(BULK_ACTIONS_EVENTS.BULK_FILE_OWNER_HELP_DISMISS, {
        action: 'help_dismiss',
        file_owner_update_type: 'bulk_file_owner_update',
        page_context: 'renters_dashboard',
        detail_type: 'bulk_actions',
        debug_info: {
          component_name: 'TeamRentersBulkFileOwnerModal',
          function_name: 'renderModalContent',
          timestamp: new Date().toISOString(),
        },
        ...properties,
      } as BulkActionsEventProperties);
    },

    trackBulkFileOwnerHelpShow: (
      properties: Partial<BulkActionsEventProperties> = {}
    ) => {
      trackEvent(BULK_ACTIONS_EVENTS.BULK_FILE_OWNER_HELP_SHOW, {
        action: 'help_show',
        file_owner_update_type: 'bulk_file_owner_update',
        page_context: 'renters_dashboard',
        detail_type: 'bulk_actions',
        debug_info: {
          component_name: 'TeamRentersBulkFileOwnerModal',
          function_name: 'renderModalContent',
          timestamp: new Date().toISOString(),
        },
        ...properties,
      } as BulkActionsEventProperties);
    },

    // Bulk dates tracking functions
    trackBulkDatesModalOpen: (
      certificationCount: number,
      properties: Partial<BulkActionsEventProperties> = {}
    ) => {
      trackEvent(BULK_ACTIONS_EVENTS.BULK_DATES_MODAL_OPEN, {
        action: 'modal_open',
        certification_count: certificationCount,
        page_context: 'renters_dashboard',
        detail_type: 'bulk_actions',
        debug_info: {
          component_name: 'TeamRentersBulkDatesModal',
          function_name: 'useEffect',
          timestamp: new Date().toISOString(),
        },
        ...properties,
      } as BulkActionsEventProperties);
    },
    trackBulkDatesModalClose: (
      certificationCount: number,
      properties: Partial<BulkActionsEventProperties> = {}
    ) => {
      trackEvent(BULK_ACTIONS_EVENTS.BULK_DATES_MODAL_CLOSE, {
        action: 'modal_close',
        certification_count: certificationCount,
        page_context: 'renters_dashboard',
        detail_type: 'bulk_actions',
        debug_info: {
          component_name: 'TeamRentersBulkDatesModal',
          function_name: 'handleDialogClose',
          timestamp: new Date().toISOString(),
        },
        ...properties,
      } as BulkActionsEventProperties);
    },
    trackBulkDatesUpdateStart: (
      certificationCount: number,
      properties: Partial<BulkActionsEventProperties> = {}
    ) => {
      trackEvent(BULK_ACTIONS_EVENTS.BULK_DATES_UPDATE_START, {
        action: 'update_start',
        certification_count: certificationCount,
        page_context: 'renters_dashboard',
        detail_type: 'bulk_actions',
        debug_info: {
          component_name: 'TeamRentersBulkDatesModal',
          function_name: 'handleSubmit',
          timestamp: new Date().toISOString(),
        },
        ...properties,
      } as BulkActionsEventProperties);
    },
    trackBulkDatesUpdateSuccess: (
      certificationCount: number,
      successCount: number,
      failureCount: number,
      properties: Partial<BulkActionsEventProperties> = {}
    ) => {
      trackEvent(BULK_ACTIONS_EVENTS.BULK_DATES_UPDATE_SUCCESS, {
        action: 'update_success',
        certification_count: certificationCount,
        success_count: successCount,
        failure_count: failureCount,
        page_context: 'renters_dashboard',
        detail_type: 'bulk_actions',
        debug_info: {
          component_name: 'TeamRentersBulkDatesModal',
          function_name: 'handleSubmit',
          timestamp: new Date().toISOString(),
        },
        ...properties,
      } as BulkActionsEventProperties);
    },
    trackBulkDatesUpdateFailure: (
      certificationCount: number,
      properties: Partial<BulkActionsEventProperties> & {
        error_message: string;
      }
    ) => {
      trackEvent(BULK_ACTIONS_EVENTS.BULK_DATES_UPDATE_FAILURE, {
        action: 'update_failure',
        certification_count: certificationCount,
        error_message: properties.error_message,
        page_context: 'renters_dashboard',
        detail_type: 'bulk_actions',
        debug_info: {
          component_name: 'TeamRentersBulkDatesModal',
          function_name: 'handleSubmit',
          timestamp: new Date().toISOString(),
        },
        ...properties,
      } as BulkActionsEventProperties);
    },
    trackBulkDatesSelectorChange: (
      properties: Partial<BulkActionsEventProperties> = {}
    ) => {
      trackEvent(BULK_ACTIONS_EVENTS.BULK_DATES_SELECTOR_CHANGE, {
        action: 'selector_change',
        page_context: 'renters_dashboard',
        detail_type: 'bulk_actions',
        debug_info: {
          component_name: 'TeamRentersBulkDatesModal',
          function_name: 'handleDateChange',
          timestamp: new Date().toISOString(),
        },
        ...properties,
      } as BulkActionsEventProperties);
    },
    trackBulkDatesHelpShow: (
      properties: Partial<BulkActionsEventProperties> = {}
    ) => {
      trackEvent(BULK_ACTIONS_EVENTS.BULK_DATES_HELP_SHOW, {
        action: 'help_show',
        page_context: 'renters_dashboard',
        detail_type: 'bulk_actions',
        debug_info: {
          component_name: 'TeamRentersBulkDatesModal',
          function_name: 'useEffect',
          timestamp: new Date().toISOString(),
        },
        ...properties,
      } as BulkActionsEventProperties);
    },

    // User Profile specific tracking
    trackCardHelpClick: (
      cardName: UserProfileCardType,
      helpType: string,
      componentName?: string,
      functionName?: string
    ) => {
      trackEvent(USER_PROFILE_EVENTS.HELP_ICON_CLICK, {
        card_name: cardName,
        card_display_name: getCardDisplayName(cardName),
        action_type: 'help_click',
        help_type: helpType,
        context: 'user_profile_page',
        detail_type: 'page',
        debug_info: createDebugInfo(componentName, functionName),
      });
    },

    trackCardExpand: (
      cardName: UserProfileCardType,
      componentName?: string,
      functionName?: string
    ) => {
      console.log('🔍 [User Profile Tracking] Card expanded:', {
        card_name: cardName,
        card_display_name: getCardDisplayName(cardName),
        component_name: componentName,
        function_name: functionName,
      });

      trackEvent(USER_PROFILE_EVENTS.CARD_INTERACTION, {
        card_name: cardName,
        card_display_name: getCardDisplayName(cardName),
        action_type: 'expand',
        card_expanded: true,
        page_context: 'user_settings',
        detail_type: 'page',
        debug_info: createDebugInfo(componentName, functionName),
      });
    },

    trackCardCollapse: (
      cardName: UserProfileCardType,
      componentName?: string,
      functionName?: string
    ) => {
      console.log('🔍 [User Profile Tracking] Card collapsed:', {
        card_name: cardName,
        card_display_name: getCardDisplayName(cardName),
        component_name: componentName,
        function_name: functionName,
      });

      trackEvent(USER_PROFILE_EVENTS.CARD_INTERACTION, {
        card_name: cardName,
        card_display_name: getCardDisplayName(cardName),
        action_type: 'collapse',
        card_expanded: false,
        page_context: 'user_settings',
        detail_type: 'page',
        debug_info: createDebugInfo(componentName, functionName),
      });
    },

    trackCardSettingChange: (
      cardName: UserProfileCardType,
      settingCategory: string,
      settingName: string,
      oldValue: unknown,
      newValue: unknown,
      componentName?: string,
      functionName?: string
    ) => {
      console.log('🔍 [User Profile Tracking] Setting changed:', {
        card_name: cardName,
        card_display_name: getCardDisplayName(cardName),
        setting_category: settingCategory,
        setting_name: settingName,
        old_value: oldValue,
        new_value: newValue,
        component_name: componentName,
        function_name: functionName,
      });

      trackEvent(USER_PROFILE_EVENTS.SETTINGS_CHANGE, {
        card_name: cardName,
        card_display_name: getCardDisplayName(cardName),
        action_type: 'setting_change',
        setting_category: settingCategory,
        setting_name: settingName,
        old_value: oldValue,
        new_value: newValue,
        page_context: 'user_settings',
        detail_type: 'page',
        debug_info: createDebugInfo(componentName, functionName),
      });
    },

    trackCardFormSave: (
      cardName: UserProfileCardType,
      success: boolean,
      errors?: string[],
      componentName?: string,
      functionName?: string
    ) => {
      console.log('🔍 [User Profile Tracking] Form save:', {
        card_name: cardName,
        card_display_name: getCardDisplayName(cardName),
        success,
        errors,
        component_name: componentName,
        function_name: functionName,
      });

      trackEvent(USER_PROFILE_EVENTS.FORM_SAVE, {
        card_name: cardName,
        card_display_name: getCardDisplayName(cardName),
        action_type: 'save',
        form_success: success,
        form_errors: errors,
        page_context: 'user_settings',
        detail_type: 'page',
        debug_info: createDebugInfo(componentName, functionName),
      });
    },

    trackCardFormCancel: (
      cardName: UserProfileCardType,
      componentName?: string,
      functionName?: string
    ) => {
      console.log('🔍 [User Profile Tracking] Form cancel:', {
        card_name: cardName,
        card_display_name: getCardDisplayName(cardName),
        component_name: componentName,
        function_name: functionName,
      });

      trackEvent(USER_PROFILE_EVENTS.FORM_CANCEL, {
        card_name: cardName,
        card_display_name: getCardDisplayName(cardName),
        action_type: 'cancel',
        page_context: 'user_settings',
        detail_type: 'page',
        debug_info: createDebugInfo(componentName, functionName),
      });
    },

    trackPasswordReset: (
      cardName: UserProfileCardType,
      componentName?: string,
      functionName?: string
    ) => {
      console.log('🔍 [User Profile Tracking] Password reset:', {
        card_name: cardName,
        card_display_name: getCardDisplayName(cardName),
        component_name: componentName,
        function_name: functionName,
      });

      trackEvent(USER_PROFILE_EVENTS.PASSWORD_RESET, {
        card_name: cardName,
        card_display_name: getCardDisplayName(cardName),
        action_type: 'password_reset',
        page_context: 'user_settings',
        detail_type: 'page',
        debug_info: createDebugInfo(componentName, functionName),
      });
    },

    trackEmailChange: (
      cardName: UserProfileCardType,
      oldEmail: string,
      newEmail: string,
      componentName?: string,
      functionName?: string
    ) => {
      console.log('🔍 [User Profile Tracking] Email change:', {
        card_name: cardName,
        card_display_name: getCardDisplayName(cardName),
        old_email: oldEmail,
        new_email: newEmail,
        component_name: componentName,
        function_name: functionName,
      });

      trackEvent(USER_PROFILE_EVENTS.EMAIL_CHANGE, {
        card_name: cardName,
        card_display_name: getCardDisplayName(cardName),
        action_type: 'email_change',
        old_value: oldEmail,
        new_value: newEmail,
        page_context: 'user_settings',
        detail_type: 'page',
        debug_info: createDebugInfo(componentName, functionName),
      });
    },

    trackPhoneChange: (
      cardName: UserProfileCardType,
      oldPhone: string,
      newPhone: string,
      componentName?: string,
      functionName?: string
    ) => {
      console.log('🔍 [User Profile Tracking] Phone change:', {
        card_name: cardName,
        card_display_name: getCardDisplayName(cardName),
        old_phone: oldPhone,
        new_phone: newPhone,
        component_name: componentName,
        function_name: functionName,
      });

      trackEvent(USER_PROFILE_EVENTS.PHONE_CHANGE, {
        card_name: cardName,
        card_display_name: getCardDisplayName(cardName),
        action_type: 'phone_change',
        old_value: oldPhone,
        new_value: newPhone,
        page_context: 'user_settings',
        detail_type: 'page',
        debug_info: createDebugInfo(componentName, functionName),
      });
    },

    trackContactEmailAdd: (
      cardName: UserProfileCardType,
      email: string,
      componentName?: string,
      functionName?: string
    ) => {
      console.log('🔍 [User Profile Tracking] Contact email added:', {
        card_name: cardName,
        card_display_name: getCardDisplayName(cardName),
        email: email,
        component_name: componentName,
        function_name: functionName,
      });

      trackEvent(USER_PROFILE_EVENTS.FORM_SAVE, {
        card_name: cardName,
        card_display_name: getCardDisplayName(cardName),
        action_type: 'contact_email_add',
        form_type: 'contact',
        form_success: true,
        page_context: 'user_settings',
        detail_type: 'page',
        debug_info: createDebugInfo(componentName, functionName),
      });
    },

    trackContactEmailDelete: (
      cardName: UserProfileCardType,
      email: string,
      componentName?: string,
      functionName?: string
    ) => {
      console.log('🔍 [User Profile Tracking] Contact email deleted:', {
        card_name: cardName,
        card_display_name: getCardDisplayName(cardName),
        email: email,
        component_name: componentName,
        function_name: functionName,
      });

      trackEvent(USER_PROFILE_EVENTS.FORM_SAVE, {
        card_name: cardName,
        card_display_name: getCardDisplayName(cardName),
        action_type: 'contact_email_delete',
        form_type: 'contact',
        form_success: true,
        page_context: 'user_settings',
        detail_type: 'page',
        debug_info: createDebugInfo(componentName, functionName),
      });
    },

    trackContactEmailSetPrimary: (
      cardName: UserProfileCardType,
      email: string,
      componentName?: string,
      functionName?: string
    ) => {
      console.log('🔍 [User Profile Tracking] Contact email set as primary:', {
        card_name: cardName,
        card_display_name: getCardDisplayName(cardName),
        email: email,
        component_name: componentName,
        function_name: functionName,
      });

      trackEvent(USER_PROFILE_EVENTS.SETTINGS_CHANGE, {
        card_name: cardName,
        card_display_name: getCardDisplayName(cardName),
        action_type: 'contact_email_set_primary',
        setting_category: 'contact',
        setting_name: 'primary_email',
        new_value: email,
        page_context: 'user_settings',
        detail_type: 'page',
        debug_info: createDebugInfo(componentName, functionName),
      });
    },

    trackContactPhoneAdd: (
      cardName: UserProfileCardType,
      phone: string,
      componentName?: string,
      functionName?: string
    ) => {
      console.log('🔍 [User Profile Tracking] Contact phone added:', {
        card_name: cardName,
        card_display_name: getCardDisplayName(cardName),
        phone: phone,
        component_name: componentName,
        function_name: functionName,
      });

      trackEvent(USER_PROFILE_EVENTS.FORM_SAVE, {
        card_name: cardName,
        card_display_name: getCardDisplayName(cardName),
        action_type: 'contact_phone_add',
        form_type: 'contact',
        form_success: true,
        page_context: 'user_settings',
        detail_type: 'page',
        debug_info: createDebugInfo(componentName, functionName),
      });
    },

    trackContactPhoneDelete: (
      cardName: UserProfileCardType,
      phone: string,
      componentName?: string,
      functionName?: string
    ) => {
      console.log('🔍 [User Profile Tracking] Contact phone deleted:', {
        card_name: cardName,
        card_display_name: getCardDisplayName(cardName),
        phone: phone,
        component_name: componentName,
        function_name: functionName,
      });

      trackEvent(USER_PROFILE_EVENTS.FORM_SAVE, {
        card_name: cardName,
        card_display_name: getCardDisplayName(cardName),
        action_type: 'contact_phone_delete',
        form_type: 'contact',
        form_success: true,
        page_context: 'user_settings',
        detail_type: 'page',
        debug_info: createDebugInfo(componentName, functionName),
      });
    },

    trackContactPhoneSetPrimary: (
      cardName: UserProfileCardType,
      phone: string,
      componentName?: string,
      functionName?: string
    ) => {
      console.log('🔍 [User Profile Tracking] Contact phone set as primary:', {
        card_name: cardName,
        card_display_name: getCardDisplayName(cardName),
        phone: phone,
        component_name: componentName,
        function_name: functionName,
      });

      trackEvent(USER_PROFILE_EVENTS.SETTINGS_CHANGE, {
        card_name: cardName,
        card_display_name: getCardDisplayName(cardName),
        action_type: 'contact_phone_set_primary',
        setting_category: 'contact',
        setting_name: 'primary_phone',
        new_value: phone,
        page_context: 'user_settings',
        detail_type: 'page',
        debug_info: createDebugInfo(componentName, functionName),
      });
    },

    trackHelpModalOpen: (
      cardName: UserProfileCardType,
      modalType: string,
      componentName?: string,
      functionName?: string
    ) => {
      console.log('🔍 [User Profile Tracking] Help modal opened:', {
        card_name: cardName,
        card_display_name: getCardDisplayName(cardName),
        modal_type: modalType,
        component_name: componentName,
        function_name: functionName,
      });

      trackEvent(USER_PROFILE_EVENTS.HELP_MODAL_OPEN, {
        card_name: cardName,
        card_display_name: getCardDisplayName(cardName),
        action_type: 'help_modal_open',
        help_type: modalType,
        page_context: 'user_settings',
        detail_type: 'page',
        debug_info: createDebugInfo(componentName, functionName),
      });
    },

    trackHelpModalClose: (
      cardName: UserProfileCardType,
      modalType: string,
      componentName?: string,
      functionName?: string
    ) => {
      console.log('🔍 [User Profile Tracking] Help modal closed:', {
        card_name: cardName,
        card_display_name: getCardDisplayName(cardName),
        modal_type: modalType,
        component_name: componentName,
        function_name: functionName,
      });

      trackEvent(USER_PROFILE_EVENTS.HELP_MODAL_CLOSE, {
        card_name: cardName,
        card_display_name: getCardDisplayName(cardName),
        action_type: 'help_modal_close',
        help_type: modalType,
        page_context: 'user_settings',
        detail_type: 'page',
        debug_info: createDebugInfo(componentName, functionName),
      });
    },

    // Tour specific tracking
    trackTourStart: (
      tourName: string,
      totalSteps: number,
      stepName?: string,
      componentName?: string,
      functionName?: string
    ) => {
      const tourDisplayName = getTourDisplayName(tourName);
      const pageContext = getPageContext(tourName);

      logTourEvent('Tour started', {
        tour_name: tourName,
        tour_display_name: tourDisplayName,
        total_steps: totalSteps,
        step_name: stepName,
        component_name: componentName,
        function_name: functionName,
      });

      trackEvent('start', {
        tour_name: tourName,
        tour_display_name: tourDisplayName,
        action: 'start',
        step_name: stepName,
        step_number: 1,
        navigation_method: 'manual',
        total_steps: totalSteps,
        page_context: pageContext,
        detail_type: 'tour',
        debug_info: createDebugInfo(componentName, functionName),
      });
    },

    trackTourComplete: (
      tourName: string,
      stepsCompleted: number,
      stepName?: string,
      componentName?: string,
      functionName?: string
    ) => {
      const tourDisplayName = getTourDisplayName(tourName);
      const pageContext = getPageContext(tourName);

      logTourEvent('Tour completed', {
        tour_name: tourName,
        tour_display_name: tourDisplayName,
        steps_completed: stepsCompleted,
        step_name: stepName,
        component_name: componentName,
        function_name: functionName,
      });

      trackEvent(TOUR_EVENTS.TOUR_COMPLETE, {
        tour_name: tourName,
        tour_display_name: tourDisplayName,
        action: 'complete',
        step_name: stepName,
        step_number: stepsCompleted,
        navigation_method: 'manual',
        total_steps: stepsCompleted,
        steps_completed: stepsCompleted,
        page_context: pageContext,
        detail_type: 'tour',
        debug_info: createDebugInfo(componentName, functionName),
      });
    },

    trackTourSkip: (
      tourName: string,
      stepNumber: number,
      stepName?: string,
      totalSteps?: number,
      componentName?: string,
      functionName?: string
    ) => {
      const tourDisplayName = getTourDisplayName(tourName);
      const pageContext = getPageContext(tourName);

      logTourEvent('Tour skipped', {
        tour_name: tourName,
        tour_display_name: tourDisplayName,
        step_number: stepNumber,
        step_name: stepName,
        component_name: componentName,
        function_name: functionName,
      });

      trackEvent(TOUR_EVENTS.TOUR_SKIP, {
        tour_name: tourName,
        tour_display_name: tourDisplayName,
        action: 'skip',
        step_name: stepName,
        step_number: stepNumber,
        navigation_method: 'manual',
        total_steps: totalSteps || 0,
        page_context: pageContext,
        detail_type: 'tour',
        debug_info: createDebugInfo(componentName, functionName),
      });
    },

    trackTourStepAdvance: (
      tourName: string,
      stepNumber: number,
      stepName: string,
      componentName?: string,
      functionName?: string
    ) => {
      const tourDisplayName = getTourDisplayName(tourName);
      const pageContext = getPageContext(tourName);

      logTourEvent('Tour step advanced', {
        tour_name: tourName,
        tour_display_name: tourDisplayName,
        step_number: stepNumber,
        step_name: stepName,
        component_name: componentName,
        function_name: functionName,
      });

      trackEvent(TOUR_EVENTS.TOUR_STEP_ADVANCE, {
        tour_name: tourName,
        tour_display_name: tourDisplayName,
        action: 'step_advance',
        step_number: stepNumber,
        step_name: stepName,
        navigation_method: 'manual',
        page_context: pageContext,
        detail_type: 'tour',
        debug_info: createDebugInfo(componentName, functionName),
      });
    },

    trackTourStepBack: (
      tourName: string,
      stepNumber: number,
      stepName: string,
      componentName?: string,
      functionName?: string
    ) => {
      const tourDisplayName = getTourDisplayName(tourName);
      const pageContext = getPageContext(tourName);

      logTourEvent('Tour step back', {
        tour_name: tourName,
        tour_display_name: tourDisplayName,
        step_number: stepNumber,
        step_name: stepName,
        component_name: componentName,
        function_name: functionName,
      });

      trackEvent(TOUR_EVENTS.TOUR_STEP_BACK, {
        tour_name: tourName,
        tour_display_name: tourDisplayName,
        action: 'step_back',
        step_number: stepNumber,
        step_name: stepName,
        navigation_method: 'manual',
        page_context: pageContext,
        detail_type: 'tour',
        debug_info: createDebugInfo(componentName, functionName),
      });
    },

    // Enhanced tour tracking functions
    trackTourPause: (
      tourName: string,
      stepNumber: number,
      stepName?: string,
      totalSteps?: number,
      componentName?: string,
      functionName?: string
    ) => {
      const tourDisplayName = getTourDisplayName(tourName);
      const pageContext = getPageContext(tourName);

      logTourEvent('Tour paused', {
        tour_name: tourName,
        tour_display_name: tourDisplayName,
        step_number: stepNumber,
        step_name: stepName,
        component_name: componentName,
        function_name: functionName,
      });

      trackEvent(TOUR_EVENTS.TOUR_PAUSE, {
        tour_name: tourName,
        tour_display_name: tourDisplayName,
        action: 'pause',
        step_name: stepName,
        step_number: stepNumber,
        navigation_method: 'manual',
        total_steps: totalSteps || 0,
        page_context: pageContext,
        detail_type: 'tour',
        debug_info: createDebugInfo(componentName, functionName),
      });
    },

    trackTourResume: (
      tourName: string,
      stepNumber: number,
      stepName?: string,
      totalSteps?: number,
      componentName?: string,
      functionName?: string
    ) => {
      const tourDisplayName = getTourDisplayName(tourName);
      const pageContext = getPageContext(tourName);

      logTourEvent('Tour resumed', {
        tour_name: tourName,
        tour_display_name: tourDisplayName,
        step_number: stepNumber,
        step_name: stepName,
        component_name: componentName,
        function_name: functionName,
      });

      trackEvent(TOUR_EVENTS.TOUR_RESUME, {
        tour_name: tourName,
        tour_display_name: tourDisplayName,
        action: 'resume',
        step_name: stepName,
        step_number: stepNumber,
        navigation_method: 'manual',
        total_steps: totalSteps || 0,
        page_context: pageContext,
        detail_type: 'tour',
        debug_info: createDebugInfo(componentName, functionName),
      });
    },

    trackTourAutoAdvance: (
      tourName: string,
      stepNumber: number,
      stepName: string,
      totalSteps?: number,
      componentName?: string,
      functionName?: string
    ) => {
      const tourDisplayName = getTourDisplayName(tourName);
      const pageContext = getPageContext(tourName);

      logTourEvent('Tour auto-advanced', {
        tour_name: tourName,
        tour_display_name: tourDisplayName,
        step_number: stepNumber,
        step_name: stepName,
        component_name: componentName,
        function_name: functionName,
      });

      trackEvent(TOUR_EVENTS.TOUR_AUTO_ADVANCE, {
        tour_name: tourName,
        tour_display_name: tourDisplayName,
        action: 'auto_advance',
        step_number: stepNumber,
        step_name: stepName,
        navigation_method: 'auto',
        total_steps: totalSteps || 0,
        page_context: pageContext,
        detail_type: 'tour',
        debug_info: createDebugInfo(componentName, functionName),
      });
    },

    trackTourStepView: (
      tourName: string,
      stepNumber: number,
      stepName: string,
      stepSelector: string,
      totalSteps?: number,
      componentName?: string,
      functionName?: string
    ) => {
      const tourDisplayName = getTourDisplayName(tourName);
      const pageContext = getPageContext(tourName);
      const stepCategory = getStepCategory({ selector: stepSelector });

      logTourEvent('Tour step viewed', {
        tour_name: tourName,
        tour_display_name: tourDisplayName,
        step_number: stepNumber,
        step_name: stepName,
        step_selector: stepSelector,
        step_category: stepCategory,
        component_name: componentName,
        function_name: functionName,
      });

      trackEvent(TOUR_EVENTS.TOUR_STEP_VIEW, {
        tour_name: tourName,
        tour_display_name: tourDisplayName,
        action: 'step_view',
        step_number: stepNumber,
        step_name: stepName,
        navigation_method: 'manual',
        total_steps: totalSteps || 0,
        page_context: pageContext,
        detail_type: 'tour',
        debug_info: createDebugInfo(componentName, functionName),
      });
    },

    trackTourClose: (
      tourName: string,
      stepNumber: number,
      stepsCompleted: number,
      stepName?: string,
      componentName?: string,
      functionName?: string
    ) => {
      const tourDisplayName = getTourDisplayName(tourName);
      const pageContext = getPageContext(tourName);

      logTourEvent('Tour closed', {
        tour_name: tourName,
        tour_display_name: tourDisplayName,
        step_number: stepNumber,
        step_name: stepName,
        steps_completed: stepsCompleted,
        component_name: componentName,
        function_name: functionName,
      });

      trackEvent(TOUR_EVENTS.TOUR_CLOSE, {
        tour_name: tourName,
        tour_display_name: tourDisplayName,
        action: 'close',
        step_name: stepName,
        step_number: stepNumber,
        navigation_method: 'manual',
        total_steps: stepsCompleted,
        steps_completed: stepsCompleted,
        page_context: pageContext,
        detail_type: 'tour',
        debug_info: createDebugInfo(componentName, functionName),
      });
    },

    // Header tracking functions
    trackHeaderHelpClick: (
      pageContext: string,
      componentName?: string,
      functionName?: string
    ) => {
      trackEvent(HEADER_EVENTS.HEADER_HELP_CLICK, {
        action_type: 'header_help_click',
        help_type: 'header_help',
        page_context: pageContext as 'user_settings' | 'other',
        detail_type: 'page',
        debug_info: createDebugInfo(componentName, functionName),
      });
    },

    trackHeaderTourClick: (
      pageContext: string,
      componentName?: string,
      functionName?: string
    ) => {
      trackEvent(HEADER_EVENTS.HEADER_TOUR_CLICK, {
        action_type: 'header_tour_click',
        help_type: 'header_tour',
        page_context: pageContext as 'user_settings' | 'other',
        detail_type: 'page',
        debug_info: createDebugInfo(componentName, functionName),
      });
    },

    trackError,
    trackPerformance,
  };
};
